import React, { DragEvent, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { formatPicture } from '../../core/services/image.service';
import { ImagePlaceholder } from './image-placeholder';
import { COVER_MIME_TYPES, COVER_SIZE } from './cover-format';
import type { Frame } from '../../lib/utils/transform-image';
import { toastPool } from '../../features/toasts/models/toast-pool';
import { Toast } from '../../features/toasts/models/toast';
import { CircularProgressIndicator } from '../circular-progress-bar';

import styles from './cover-image.module.scss';
interface CoverImageProps {
  title: string;
  src?: string;
  progress?: number;
  onEdit?: (file: Frame) => void;
  onDoubleClick?: () => void;
  onClick?: () => void;
  /**
   * The resource's own glyph, shown centred on a grey field when there is no cover. Opting in
   * replaces the "Add photo" uploader, so the consumer owns the way in — a card offers "Edit
   * cover…" in its menu. Left unset, the uploader stays, which is what the shot and candidate
   * cards still want.
   */
  placeholderIcon?: React.ReactNode;
}

export const CoverImage: React.FC<CoverImageProps> = observer(
  ({ src, title, progress, onEdit, onDoubleClick, onClick, placeholderIcon }) => {
    const [hasError, setHasError] = useState(!src);
    const [overlay, setOverlay] = useState(false);
    const [loadingOverlay, setLoadingOverlay] = useState(false);

    useEffect(() => {
      setHasError(!src);
    }, [src]);

    useEffect(() => {
      if (progress !== undefined) {
        setLoadingOverlay(progress < 100);
      }
    }, [progress]);

    const setImageError = () => setHasError(true);

    const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();

      if (e.dataTransfer.types.find((el) => el === 'Files')) {
        setOverlay(true);
      }
    };

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setOverlay(false);
    };

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
    };

    const handleDoubleClick = () => {
      if (!onDoubleClick) {
        return;
      }

      onDoubleClick();
    };

    const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();

      if (!onEdit) {
        return;
      }

      setOverlay(false);

      const f = Array.from(e.dataTransfer.items).map((item) => item);

      if (f.filter((el) => el.kind === 'string').length > 0) {
        return;
      }

      const file = Array.from(e.dataTransfer.items)[0].getAsFile();

      if (!file) return;

      if (file.type === 'image/gif') {
        const frame: Frame = {
          data: file,
          size: file.size,
          type: file.type,
        };

        return onEdit(frame);
      }

      if (!COVER_MIME_TYPES.includes(file.type)) {
        toastPool.insert(new Toast('Invalid file type.', 'error'));
        return;
      }
      const frame = await formatPicture(file, COVER_SIZE);

      onEdit(frame);
    };

    const handleImageChange = async (file: File) => {
      if (!onEdit) {
        return;
      }

      if (file) {
        if (!COVER_MIME_TYPES.includes(file.type)) return;
        const frame = await formatPicture(file, COVER_SIZE);
        onEdit(frame);
      }
    };

    return (
      <div
        className={styles['cover-holder']}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={onClick}
      >
        {!hasError ? (
          <img
            className={styles['image']}
            loading="lazy"
            src={src}
            alt={title}
            onDoubleClick={handleDoubleClick}
            onError={setImageError}
          />
        ) : placeholderIcon ? (
          <div className={styles['icon-placeholder']}>{placeholderIcon}</div>
        ) : (
          <ImagePlaceholder onChange={onEdit && handleImageChange} />
        )}

        <div className={styles['loader-overlay']} data-show={!!loadingOverlay}>
          <CircularProgressIndicator progress={progress} displayProgress />

          <span>Image is being generated</span>
        </div>

        <div className={styles['edit-overlay']} data-show={!!overlay}>
          Edit picture
        </div>
      </div>
    );
  },
);
